import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const categories = [
  "All categories",
  "Jobs & Opportunity",
  "Infrastructure",
  "Higher Education",
  "Housing",
  "Agriculture",
  "Arts & Culture",
  "Water & Sanitation",
  "Science & Innovation",
  "Education",
];

const provinces = [
  "All provinces",
  "Gauteng",
  "Western Cape",
  "KwaZulu-Natal",
  "Eastern Cape",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Free State",
  "Northern Cape",
];

const fmt = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

export default function Projects() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All categories");
  const [province, setProvince] = useState("All provinces");
  const [sort, setSort] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("status", "approved")
      .order("created_at", { ascending: false });

    if (!error && data) setProjects(data);
    setLoading(false);
  };

  const handleNotify = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    navigate(session ? "/dashboard" : "/register");
  };

  const handleProposeProject = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    navigate(session ? "/propose-project" : "/register");
  };

  const filtered = projects
    .filter((p) => {
      const matchSearch =
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.category?.toLowerCase().includes(search.toLowerCase());
      const matchCategory = category === "All categories" || p.category === category;
      const matchProvince = province === "All provinces" || p.location === province;
      return matchSearch && matchCategory && matchProvince;
    })
    .sort((a, b) => {
      if (sort === "Most funded") return (b.raised || 0) - (a.raised || 0);
      if (sort === "Most contributors") return 0; // no contributor count tracked yet
      return 0;
    });

  const activeFilterCount =
    (category !== "All categories" ? 1 : 0) +
    (province !== "All provinces" ? 1 : 0) +
    (sort !== "" ? 1 : 0);

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", color: "#1A1A2E", minHeight: "100vh" }}>

      <style>{`
        .proj-header { padding: 32px 48px; }
        .proj-header-row { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; flex-wrap: wrap; }
        .proj-search { width: 280px; padding: 10px 16px; border: 1.5px solid #EDE8DF; border-radius: 99px; font-size: 13px; background: #F5F0E8; color: #1A1A2E; outline: none; font-family: var(--font-body); }
        .proj-filterbar { padding: 16px 48px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .proj-filterpanel { background: #fff; border: 1px solid #EDE8DF; border-radius: 14px; margin: 0 48px 16px; padding: 18px 22px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
        .proj-main { padding: 0 48px 48px; }
        .proj-select { padding: 7px 14px; border: 1px solid #EDE8DF; border-radius: 8px; font-size: 12px; background: #F5F0E8; color: #1A1A2E; outline: none; font-family: var(--font-body); cursor: pointer; }
        .proj-empty { max-width: 520px; margin: 40px auto; background: #fff; border: 1px solid #EDE8DF; border-radius: 20px; padding: 48px 32px; text-align: center; }
        .proj-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
        .proj-card { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 20px; cursor: pointer; }
        @media (max-width: 768px) {
          .proj-header { padding: 24px 20px; }
          .proj-header-row { flex-direction: column; align-items: flex-start; gap: 14px; }
          .proj-search { width: 100%; font-size: 16px; }
          .proj-filterbar { padding: 12px 20px; }
          .proj-filterpanel { margin: 0 20px 12px; padding: 14px 16px; }
          .proj-filterpanel select { width: 100%; font-size: 16px; }
          .proj-main { padding: 0 20px 32px; }
          .proj-empty { margin: 24px auto; padding: 36px 24px; }
          .proj-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <Navbar />

      {/* HEADER */}
      <div className="proj-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF" }}>
        <div className="proj-header-row">
          <div>
            <h1 style={{ fontSize: "clamp(22px, 2.5vw, 28px)", fontWeight: 900, color: "#1A1A2E", letterSpacing: "-0.02em", marginBottom: 4 }}>
              Browse all projects
            </h1>
            <p style={{ fontSize: 13, color: "#888" }}>
              Find a cause that matters to you and help build something real
            </p>
          </div>
          <input
            className="proj-search"
            type="text"
            placeholder="Search projects or categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="proj-filterbar">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #EDE8DF", borderRadius: 99, padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#4A4A6A", cursor: "pointer", fontFamily: "var(--font-body)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Filter and sort
          {activeFilterCount > 0 && (
            <span style={{ background: "#1B3A6B", color: "#fff", borderRadius: 99, fontSize: 10, fontWeight: 700, padding: "1px 7px" }}>
              {activeFilterCount}
            </span>
          )}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <button
          onClick={handleProposeProject}
          style={{ background: "rgba(201,168,76,0.1)", color: "#8a6a1a", border: "1px dashed #C9A84C", padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: "var(--font-body)", cursor: "pointer" }}>
          + Propose a project
        </button>
      </div>

      {/* FILTER PANEL */}
      {filtersOpen && (
        <div className="proj-filterpanel">
          <select className="proj-select" value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="proj-select" value={province} onChange={(e) => setProvince(e.target.value)}>
            {provinces.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select className="proj-select" style={{ marginLeft: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="">Sort by...</option>
            <option>Most funded</option>
          </select>
        </div>
      )}

      {/* MAIN */}
      <div className="proj-main">
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#888", fontSize: 14 }}>
            Loading projects...
          </div>
        ) : filtered.length === 0 && projects.length === 0 ? (
          <div className="proj-empty">
            <div style={{ width: 56, height: 56, background: "#F5F0E8", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: "#1A1A2E", marginBottom: 10 }}>
              Projects are coming soon
            </h2>
            <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, marginBottom: 24 }}>
              We're verifying our first community projects. Register now to be notified the moment they go live, or propose a project of your own.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={handleNotify}
                style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "12px 24px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
                Notify Me
              </button>
              <button
                onClick={handleProposeProject}
                style={{ background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "12px 24px", color: "#4A4A6A", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
                Propose a Project
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#888", fontSize: 14 }}>
            No projects match your filters. Try adjusting your search.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
              Showing <strong style={{ color: "#1B3A6B" }}>{filtered.length}</strong> project{filtered.length !== 1 ? "s" : ""}
            </div>
            <div className="proj-grid">
              {filtered.map((p) => {
                const pct = p.goal > 0 ? Math.min(100, Math.round((p.raised / p.goal) * 100)) : 0;
                return (
                  <div key={p.id} className="proj-card" onClick={() => navigate("/projects")}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ background: "#F5F0E8", color: "#1B3A6B", fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700 }}>
                        {p.category}
                      </span>
                      <span style={{ fontSize: 11, color: "#888" }}>{p.location}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>{p.title}</div>
                    <div style={{ fontSize: 12, color: "#888", lineHeight: 1.6, marginBottom: 14 }}>{p.description}</div>
                    <div style={{ background: "#EDE8DF", borderRadius: 99, height: 6, overflow: "hidden", marginBottom: 8 }}>
                      <div style={{ background: "#1B3A6B", height: 6, borderRadius: 99, width: `${pct}%` }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: "#1B3A6B" }}>{fmt(p.raised)}</span>
                      <span style={{ color: "#888" }}>of {fmt(p.goal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

    </div>
  );
}